import _defineProperty from "@babel/runtime/helpers/defineProperty";
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
import React, { memo, Fragment, useMemo, useCallback, useState, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { createUseStyles, useTheme, Modal, Icon, Input, Advice, SearchBar, SelectWrapper, Chip, Select, Loader } from 'hoi-poi-ui';
import { useWeb5Context } from "../../lib/Web5Context";
import OptionGroup from "./components/OptionGroup";
import OptionGroups from "./components/OptionGroups";
import { isEqual } from "../../utils/objects";
import { getSelectedFilters } from "../Filters/utils";
import styles from "./styles";
const useStyles = createUseStyles(styles, 'ModalOptions');
var _ref6 = /*#__PURE__*/React.createElement(Icon, {
  name: "search",
  color: "currentColor"
});
var _ref7 = /*#__PURE__*/React.createElement(Loader, {
  size: "massive"
});
const ModalOptions = /*#__PURE__*/memo(_ref => {
  let {
    advice,
    adviceType = 'info',
    className,
    onToggleModal,
    title,
    confirmText,
    cancelText,
    deleteText,
    placeholderSearch,
    isOpen,
    schema: baseSchema,
    selected,
    disabled: disabledProp,
    onChangeSelected,
    onConfirm,
    onCancel,
    size = 'medium',
    overrides,
    overlayClassName,
    postComponent,
    selectorsSchema,
    multiSchema,
    onResetSelected,
    defaultFilters,
    isLoading,
    inputFocus = true,
    forceSelectAllTypes = true
  } = _ref;
  const classes = useStyles();
  const theme = useTheme();
  const classNames = classnames(classes.root, className);
  const searchContainerClassName = classnames({
    [classes.multipleSelectors]: selectorsSchema === null || selectorsSchema === void 0 ? void 0 : selectorsSchema.length
  });
  const searchInputClassName = classnames(classes.search, {
    [classes.searchWithSelectors]: selectorsSchema === null || selectorsSchema === void 0 ? void 0 : selectorsSchema.length
  });
  const [search, setSearch] = useState('');
  const [disabled, setDisabled] = useState(disabledProp || []);
  const [schema, setSchema] = useState(baseSchema);
  const [type, setType] = useState([]);
  const [openDropdowns, setOpenDropdowns] = useState({});
  const mappedForDisable = useRef({});
  const isFirstLoad = useRef(true);
  const selectRefs = useRef({});
  const selectorsOptions = useRef({});
  const selectorsOptionsMapped = useRef({});
  const [initialSelected, setInitialSelected] = useState({});
  const prevIsOpen = useRef({});
  const {
    t
  } = useWeb5Context();
  const setSelectedTypes = useCallback(_ref2 => {
    let {
      types,
      overrideState,
      isFirstLoad = false
    } = _ref2;
    const oldSelected = _objectSpread({}, selected) || {};
    const newSelected = multiSchema.reduce((obj, item) => {
      const {
        id
      } = item;
      let isSelected = false;
      if (isFirstLoad && typeof item.defaultTypeState === 'boolean') {
        isSelected = item.defaultTypeState;
      } else {
        isSelected = !!types ? !!types.find(type => type.value === id) : !!overrideState;
      }
      const oldSection = oldSelected[id] || {};
      return _objectSpread(_objectSpread({}, obj), {}, {
        [id]: _objectSpread(_objectSpread({}, oldSection), {}, {
          isSelected: isSelected
        })
      });
    }, {});
    return newSelected;
  }, [multiSchema, selected]);
  const initMultiSchema = useCallback(() => {
    if (Object.keys(selected).length === 0) return;
    const hasSelectedTypes = Object.values(selected).filter(item => item.isSelected).length > 0;
    const newTypes = multiSchema.reduce((arr, el) => {
      const {
        id,
        label
      } = el;
      if (selected[id] && selected[id].isSelected) arr.push({
        label,
        value: id
      });
      return arr;
    }, []);
    setType(newTypes);
    let newSelected = selected;
    if (isFirstLoad.current) {
      newSelected = !hasSelectedTypes && forceSelectAllTypes ? setSelectedTypes({
        types: null,
        overrideState: true,
        isFirstLoad: true
      }) : setSelectedTypes({
        types: newTypes,
        overrideState: false,
        isFirstLoad: true
      });
      onChangeSelected(_objectSpread({}, newSelected));
    }
    isFirstLoad.current = false;
    return newSelected;
  }, [forceSelectAllTypes, multiSchema, onChangeSelected, selected, setSelectedTypes]);
  useEffect(() => {
    setSchema(baseSchema);
    let newSelected = selected;
    if (multiSchema) newSelected = initMultiSchema();
    if (isOpen && prevIsOpen.current !== isOpen) {
      prevIsOpen.current = true;
      setInitialSelected(newSelected);
    } else if (!isOpen) {
      prevIsOpen.current = false;
    }
  }, [baseSchema, initMultiSchema, multiSchema, selected, isOpen]);
  useEffect(() => {
    setDisabled(disabledProp || []);
  }, [disabledProp]);
  useEffect(() => {
    if (!schema && !multiSchema) return;
    const getMappedForDisable = schema => schema.reduce((obj, group) => {
      var _group$options;
      if (!(group !== null && group !== void 0 && group.options) || (group === null || group === void 0 ? void 0 : (_group$options = group.options) === null || _group$options === void 0 ? void 0 : _group$options.length) === 0) return obj;
      const options = group.options.reduce((object, option) => {
        obj[option.label] = group.id;
        return obj;
      }, {});
      obj = _objectSpread(_objectSpread({}, obj), options);
      return obj;
    }, {});
    if (multiSchema) {
      mappedForDisable.current = multiSchema.reduce((arr, _ref3) => {
        let {
          schema
        } = _ref3;
        return [...arr, getMappedForDisable(schema)];
      }, []);
    } else {
      mappedForDisable.current = getMappedForDisable(schema);
    }
  }, [multiSchema, schema]);
  const onSelect = useCallback(_ref4 => {
    let {
      id,
      parentId,
      options,
      sectionId
    } = _ref4;
    return () => {
      let newSelected = _objectSpread({}, selected) || {};
      let optionsIds = [];
      let action = '';
      if (sectionId) {
        var _newSelected$sectionI;
        newSelected = (_newSelected$sectionI = newSelected[sectionId]) !== null && _newSelected$sectionI !== void 0 && _newSelected$sectionI.options ? _objectSpread({}, newSelected[sectionId].options) : {};
      }
      if (id && options && !parentId) {
        var _disabled$sectionId, _disabled$sectionId$o, _disabled$sectionId$o2, _disabled$id, _newSelected, _newSelected$id3;
        const groupDisabledOptions = sectionId ? disabled === null || disabled === void 0 ? void 0 : (_disabled$sectionId = disabled[sectionId]) === null || _disabled$sectionId === void 0 ? void 0 : (_disabled$sectionId$o = _disabled$sectionId.options) === null || _disabled$sectionId$o === void 0 ? void 0 : (_disabled$sectionId$o2 = _disabled$sectionId$o[id]) === null || _disabled$sectionId$o2 === void 0 ? void 0 : _disabled$sectionId$o2.options : (disabled === null || disabled === void 0 ? void 0 : (_disabled$id = disabled[id]) === null || _disabled$id === void 0 ? void 0 : _disabled$id.options) || [];
        const uncheckedDisabledOptions = (groupDisabledOptions === null || groupDisabledOptions === void 0 ? void 0 : groupDisabledOptions.filter(option => {
          var _newSelected$id;
          return !((_newSelected$id = newSelected[id]) !== null && _newSelected$id !== void 0 && _newSelected$id.options.includes(option));
        })) || [];
        const checkedDisabledOptions = (groupDisabledOptions === null || groupDisabledOptions === void 0 ? void 0 : groupDisabledOptions.filter(option => {
          var _newSelected$id2;
          return (_newSelected$id2 = newSelected[id]) === null || _newSelected$id2 === void 0 ? void 0 : _newSelected$id2.options.includes(option);
        })) || [];
        if (!((_newSelected = newSelected) !== null && _newSelected !== void 0 && (_newSelected$id3 = _newSelected[id]) !== null && _newSelected$id3 !== void 0 && _newSelected$id3.isSelected)) {
          newSelected[id] = {
            isSelected: true,
            options: options.filter(option => !uncheckedDisabledOptions.includes(option.id)).map(current => {
              return current.id;
            })
          };
          action = 'select';
        } else {
          newSelected[id] = {
            isSelected: false,
            options: checkedDisabledOptions
          };
          action = 'unselect';
        }
      } else if (id && parentId) {
        var _newSelected2;
        if (!((_newSelected2 = newSelected) !== null && _newSelected2 !== void 0 && _newSelected2[parentId])) {
          newSelected[parentId] = {
            isSelected: false,
            options: [id]
          };
          if (options.length === newSelected[parentId].options.length) {
            newSelected[parentId].isSelected = true;
          }
          action = 'select';
        } else if (!newSelected[parentId].options.includes(id)) {
          newSelected[parentId].options.push(id);
          if (options.length === newSelected[parentId].options.length) {
            newSelected[parentId].isSelected = true;
          }
          action = 'select';
        } else {
          const newOptions = newSelected[parentId].options.filter(current => current !== id);
          newSelected[parentId] = {
            isSelected: false,
            options: newOptions
          };
          action = 'unselect';
        }
      }
      if (sectionId) {
        const oldSelected = _objectSpread({}, selected) || {};
        const oldSection = oldSelected[sectionId] || {};
        const newSectionObj = _objectSpread(_objectSpread({}, oldSelected), {}, {
          [sectionId]: _objectSpread(_objectSpread({}, oldSection), {}, {
            options: newSelected
          })
        });
        newSelected = _objectSpread({}, newSectionObj);
      }
      if (parentId) {
        optionsIds = [id];
      } else {
        optionsIds = (options === null || options === void 0 ? void 0 : options.length) > 0 && options.map(current => current.id) || [];
      }
      const info = {
        clickedIds: optionsIds,
        action
      };
      onChangeSelected(newSelected, info);
    };
  }, [selected, onChangeSelected, disabled]);
  const getDisableds = useCallback(value => {
    if (value) {
      const getNewDisabled = currentSchema => currentSchema.reduce((obj, el) => {
        var _disabled$el$id, _disabledProp$el$id;
        return _objectSpread(_objectSpread({}, obj), {}, {
          [el.id]: {
            options: (disabled === null || disabled === void 0 ? void 0 : (_disabled$el$id = disabled[el.id]) === null || _disabled$el$id === void 0 ? void 0 : _disabled$el$id.options) || [],
            isDisabled: !el.label.toLowerCase().includes(value.trim().toLowerCase()) || (disabledProp === null || disabledProp === void 0 ? void 0 : (_disabledProp$el$id = disabledProp[el.id]) === null || _disabledProp$el$id === void 0 ? void 0 : _disabledProp$el$id.isDisabled)
          }
        });
      }, {});
      const newDisabled = multiSchema ? multiSchema.reduce((obj, el) => _objectSpread(_objectSpread({}, obj), {}, {
        [el.id]: {
          options: getNewDisabled(el.schema)
        }
      }), {}) : getNewDisabled(schema);
      setDisabled(newDisabled);
    } else {
      setDisabled(disabledProp || {});
    }
  }, [disabled, disabledProp, multiSchema, schema]);
  const handleChangeType = useCallback(idSelector => types => {
    let newSelected = [];
    let newTypes = [];
    if (idSelector) {
      // To simplify other components and props, we keep working with a single list of selected and multiSchema.
      // Since we have a single list of selected and two or more selectors whe have to treat the new selected options.
      const otherOptions = Object.entries(selectorsOptions.current).reduce((arr, _ref5) => {
        let [key, value] = _ref5;
        if (key !== idSelector) {
          arr = [...arr, ...value];
        }
        return arr;
      }, []);
      const otherSelectedOptions = otherOptions.filter(current => {
        var _selected$current$val;
        return (selected === null || selected === void 0 ? void 0 : (_selected$current$val = selected[current.value]) === null || _selected$current$val === void 0 ? void 0 : _selected$current$val.isSelected) || false;
      });
      newTypes = [...(types || []), ...otherSelectedOptions];
      newSelected = setSelectedTypes({
        types: newTypes
      });
    } else {
      newSelected = setSelectedTypes({
        types
      });
      newTypes = types;
    }
    onChangeSelected(_objectSpread({}, newSelected));
    setType(newTypes);
  }, [onChangeSelected, setSelectedTypes, selected]);
  const onClear = useCallback(selectorId => {
    return e => {
      e.stopPropagation();
      const newTypes = type.filter(item => {
        var _selectorsOptionsMapp, _selectorsOptionsMapp2;
        return !((_selectorsOptionsMapp = selectorsOptionsMapped.current) !== null && _selectorsOptionsMapp !== void 0 && (_selectorsOptionsMapp2 = _selectorsOptionsMapp[selectorId]) !== null && _selectorsOptionsMapp2 !== void 0 && _selectorsOptionsMapp2.includes(item.value));
      });
      const newSelected = setSelectedTypes({
        types: newTypes
      });
      onChangeSelected(_objectSpread({}, newSelected));
      setType(newTypes);
    };
  }, [type, setSelectedTypes, onChangeSelected]);
  const onSearch = useCallback(value => {
    getDisableds(value);
    setSearch(value);
  }, [getDisableds]);
  const onToggle = useCallback((sectionId, status) => {
    const oldSelected = _objectSpread({}, selected) || {};
    const oldSection = oldSelected[sectionId] || {};
    onChangeSelected(_objectSpread(_objectSpread({}, oldSelected), {}, {
      [sectionId]: _objectSpread(_objectSpread({}, oldSection), {}, {
        isExpanded: status
      })
    }));
  }, [onChangeSelected, selected]);
  const handleToggleModal = useCallback(() => {
    setSearch('');
    setDisabled(disabledProp);
    setType([]);
    onToggleModal && onToggleModal();
  }, [disabledProp, onToggleModal]);
  const handleCancel = useCallback(() => {
    onCancel && onCancel();
    handleToggleModal();
  }, [onCancel, handleToggleModal]);
  const handleConfirm = useCallback(() => {
    onConfirm && onConfirm(selected);
    handleToggleModal();
  }, [selected, handleToggleModal, onConfirm]);
  const typeOptions = useMemo(() => {
    if (!multiSchema) return;
    return multiSchema.reduce((arr, item) => [...arr, {
      label: item.label,
      value: item.id
    }], []);
  }, [multiSchema]);
  const options = useMemo(() => {
    if (!schema && !multiSchema) return;
    const Options = multiSchema ? OptionGroups : OptionGroup;
    const optionsProps = {
      disabled,
      onSelect,
      search,
      selected
    };
    if (schema) optionsProps.schema = schema;
    if (multiSchema) {
      optionsProps.multiSchema = multiSchema;
      optionsProps.onToggle = onToggle;
      optionsProps.type = type;
    }
    return /*#__PURE__*/React.createElement(Options, optionsProps);
  }, [disabled, multiSchema, onSelect, onToggle, schema, search, selected, type]);
  const selectOverrides = useMemo(() => {
    return {
      styles: {
        menu: base => _objectSpread(_objectSpread({}, base), {}, {
          position: 'relative',
          boxShadow: 'none !important',
          margin: 0,
          paddingBottom: 0
        }),
        menuList: base => _objectSpread(_objectSpread({}, base), {}, {
          paddingBottom: 0
        }, theme.utils.scrollbar)
      },
      option: {
        style: {
          borderRadius: 4
        }
      }
    };
  }, [theme]);
  const getIsOpenValueList = useCallback(fieldId => {
    return isOpen => {
      setOpenDropdowns(_objectSpread(_objectSpread({}, openDropdowns), {}, {
        [fieldId]: isOpen
      }));
      setTimeout(() => {
        var _selectRefs$current;
        if (isOpen && selectRefs !== null && selectRefs !== void 0 && (_selectRefs$current = selectRefs.current) !== null && _selectRefs$current !== void 0 && _selectRefs$current[fieldId]) {
          selectRefs.current[fieldId].focus();
        }
      }, 50);
    };
  }, [openDropdowns, setOpenDropdowns]);
  const renderSelectors = useMemo(() => {
    if (!(selectorsSchema !== null && selectorsSchema !== void 0 && selectorsSchema.length)) return null;
    return selectorsSchema.reduce((arr, current, index) => {
      var _current$schema;
      if (!(current !== null && current !== void 0 && (_current$schema = current.schema) !== null && _current$schema !== void 0 && _current$schema.length)) return null;
      if (current.schema) {
        const idOptions = [];
        const optionsFiltered = current.schema.reduce((arr, item) => {
          idOptions.push(item.id);
          return [...arr, {
            label: item.label,
            value: item.id,
            iconType: item.iconType
          }];
        }, []);
        const typesFiltered = type.filter(current => idOptions.includes(current.value));
        const selectorId = `selector_${index}`;
        selectorsOptions.current[selectorId] = optionsFiltered;
        selectorsOptionsMapped.current[selectorId] = optionsFiltered.map(current => current.value);
        const isOpenDropDown = (openDropdowns === null || openDropdowns === void 0 ? void 0 : openDropdowns[selectorId]) || false;
        const chipLabel = typesFiltered.length > 0 ? `${current.label} (${typesFiltered.length})` : current.label;
        arr.push(/*#__PURE__*/React.createElement("div", {
          key: selectorId
        }, /*#__PURE__*/React.createElement(SelectWrapper, {
          getIsOpen: getIsOpenValueList(selectorId),
          classes: {
            Popover: classes.popoverCustomWide
          },
          customOptions: /*#__PURE__*/React.createElement(Fragment, null, /*#__PURE__*/React.createElement(Select, {
            options: optionsFiltered,
            value: typesFiltered,
            isMulti: true,
            isFuzzy: !current.selectAllLabel,
            isSearchable: true,
            isFullWidth: true,
            menuPosition: "absolute",
            useMenuPortal: false,
            overrides: selectOverrides,
            onChange: handleChangeType(selectorId),
            placeholder: t('label_selectone'),
            isReadOnly: false,
            getRef: ref => {
              selectRefs.current[selectorId] = ref;
            },
            selectAllLabel: current.selectAllLabel
          }))
        }, /*#__PURE__*/React.createElement(Chip, {
          size: "large",
          isUnfolded: true,
          isActive: isOpenDropDown,
          isFilled: typesFiltered.length > 0,
          isOutlinedColor: false,
          className: "",
          onRemove: typesFiltered !== null && typesFiltered !== void 0 && typesFiltered.length ? onClear(selectorId) : null
        }, chipLabel))));
      }
      return arr;
    }, []);
  }, [selectorsSchema, selectOverrides, getIsOpenValueList, openDropdowns, classes.popoverCustomWide, t, handleChangeType, type, onClear]);
  const preComponent = useMemo(() => {
    return /*#__PURE__*/React.createElement(Fragment, null, advice && /*#__PURE__*/React.createElement(Advice, {
      type: adviceType,
      className: classes.advice,
      showIcon: true,
      showCollapse: false
    }, advice), /*#__PURE__*/React.createElement("div", {
      className: searchContainerClassName
    }, /*#__PURE__*/React.createElement("div", {
      className: searchInputClassName
    }, multiSchema && !selectorsSchema ? /*#__PURE__*/React.createElement(SearchBar, {
      placeholder: placeholderSearch,
      typeOptions: typeOptions,
      type: type,
      onChangeType: handleChangeType(),
      allowMultipleTypes: true,
      selectedTypesLiteral: t('label_number_selected'),
      selectedTypesPlaceholder: t('label_none_selected'),
      useAsSimpleSearch: true,
      forceStartFocused: inputFocus,
      onChange: e => {
        var _e$target;
        return onSearch((e === null || e === void 0 ? void 0 : (_e$target = e.target) === null || _e$target === void 0 ? void 0 : _e$target.value) || null);
      }
    }) : /*#__PURE__*/React.createElement(Input, {
      placeholder: placeholderSearch,
      onChange: onSearch,
      value: search,
      preComponent: _ref6,
      autoFocus: inputFocus,
      isFullWidth: true,
      className: classes.searchInput
    })), renderSelectors));
  }, [advice, adviceType, classes.advice, classes.searchInput, multiSchema, placeholderSearch, typeOptions, type, handleChangeType, t, onSearch, search, inputFocus, selectorsSchema, renderSelectors, searchContainerClassName, searchInputClassName]);
  const selectedFilters = useMemo(() => {
    let newSelected = getSelectedFilters(selected);
    return newSelected;
  }, [selected]);
  const disableResetButton = useMemo(() => {
    if (!multiSchema) return isEqual(defaultFilters, selectedFilters);
    const totalTypes = Object.keys(selected).length;
    const totalSelected = Object.values(selected).filter(item => item.isSelected).length;
    let areTypesResetable = true;
    if (forceSelectAllTypes) {
      areTypesResetable = totalTypes !== totalSelected ? true : false;
    } else {
      areTypesResetable = !totalSelected || false;
    }
    const totalInitialSelected = Object.values(initialSelected).filter(item => item.isSelected).length;
    return isEqual(defaultFilters, selectedFilters) && totalInitialSelected === totalSelected && !areTypesResetable;
  }, [defaultFilters, selectedFilters, initialSelected, selected, forceSelectAllTypes, multiSchema]);
  const handleOnResetSelected = useCallback(() => {
    if (disableResetButton) return;
    onResetSelected && onResetSelected();
  }, [disableResetButton, onResetSelected]);
  return /*#__PURE__*/React.createElement(Modal, {
    className: classNames,
    overlayClassName: overlayClassName || classes.overlay,
    title: isLoading ? ' ' : title,
    isOpen: isOpen,
    size: size,
    useContentStaticHeight: true,
    onRequestClose: handleCancel,
    onCancel: !isLoading && handleCancel,
    onConfirm: !isLoading && handleConfirm,
    cancelText: cancelText,
    confirmText: confirmText,
    deleteText: deleteText,
    preComponent: !isLoading && preComponent,
    postComponent: !isLoading && postComponent,
    onDelete: handleOnResetSelected,
    classes: {
      deleteButton: disableResetButton ? classes.resetSelectedDisabled : classes.resetSelected
    },
    overrides: useMemo(() => _objectSpread({
      content: {
        style: isLoading ? {
          height: '100%'
        } : {
          border: `1px solid ${theme.colors.grey[100]}`,
          height: '100%',
          borderRadius: '4px'
        }
      }
    }, overrides), [isLoading, overrides, theme])
  }, isLoading && /*#__PURE__*/React.createElement("div", {
    className: classes.loader
  }, _ref7), !isLoading && /*#__PURE__*/React.createElement("div", {
    className: classes.optionsContainer
  }, options));
});
const schema = PropTypes.arrayOf(PropTypes.shape({
  label: PropTypes.string,
  id: PropTypes.string,
  options: PropTypes.arrayOf(PropTypes.shape({
    label: PropTypes.string,
    id: PropTypes.string
  }))
}));
process.env.NODE_ENV !== "production" ? ModalOptions.propTypes = {
  advice: PropTypes.string,
  adviceType: PropTypes.oneOf(['error', 'info', 'success', 'warning']),
  className: PropTypes.string,
  isOpen: PropTypes.bool,
  onToggleModal: PropTypes.func,
  // Use one of those, schema or multischema
  schema,
  multiSchema: PropTypes.arrayOf(PropTypes.shape({
    label: PropTypes.string,
    id: PropTypes.string,
    expanded: PropTypes.bool,
    defaultTypeState: PropTypes.bool,
    schema
  })),
  selected: PropTypes.object,
  //For schema
  //Ex:
  // selected: {
  //     ['info']: {
  //         isSelected: true,
  //         options: ['name', 'status', 'environment'],
  //     },
  // }
  // For multiSchema
  // Ex:
  // selected: {
  //     ['group']: {
  //         isExpanded: true,
  //         isSelected: true,
  //         options: {
  //             ['subGroup']: {
  //                 isSelected: true,
  //                 options: ['name', 'status', 'environment']
  //             }
  //         }
  //     }
  // }
  onConfirm: PropTypes.func,
  onCancel: PropTypes.func,
  size: PropTypes.oneOf(['small', 'medium', 'large']),
  overrides: PropTypes.object,
  postComponent: PropTypes.node,
  overlayClassName: PropTypes.string,
  isLoading: PropTypes.bool
} : void 0;
export default ModalOptions;